// meeting-guide-action.ts — Action standard d'un guide de réunion.
//
// Construit le pré-remplissage de l'action proposée depuis la fiche réunion.
// Dérivation pure : aucun accès à window/document, aucun appel à `t()`.
//
// L'action n'est PAS créée ici. Le CTA pose ce pré-remplissage puis ouvre
// `#action/0`, la page de création habituelle : rien n'est écrit tant que
// l'utilisateur n'enregistre pas, et il peut tout ajuster avant.

import type { GuideItem } from './meeting-guides.js';
import type { MeetingFormState } from './meeting-form.js';

/** Priorité « Normale » du référentiel d'actions (cf. actionModal.priority.2). */
export const ACTION_PRIORITY_NORMAL = 2;

/**
 * Note système posée à la création. `POST /actions` la crée dans la même
 * transaction que l'action, avec `user_id` NULL — c'est une trace, pas une
 * intervention de l'utilisateur.
 *
 * Elle nomme la réunion : contrairement au préfixe du libellé, qui est
 * optionnel, la note dit toujours d'où vient l'action.
 */
export function meetingOriginNote(ctx: MeetingContext): string {
  return `Création de l'action à partir de la réunion ${ctx.meetingLabel} du ${frDate(ctx.meetingDate)}`;
}

export interface MeetingContext {
  /** Libellé de la réunion, cité en tête de l'action si le guide le demande. */
  meetingLabel: string;
  /** Date de la réunion, `YYYY-MM-DD`. Toujours renseignée : la colonne est
   *  NOT NULL et le formulaire refuse d'enregistrer sans elle. */
  meetingDate: string;
  animatorIds: number[];
  participantIds: number[];
  projectId: number | null;
  committeeId: number | null;
  structureId: number | null;
  /** Utilisateur connecté : il approuve l'action qu'il crée. */
  currentUserId: number;
}

export interface ActionPrefill {
  text: string;
  deadline: string | null;
  status_id: number | null;
  priority: number;
  approver_id: number;
  performers: number[];
  consulted: number[];
  informed: number[];
  /** Confidentielle : une action de préparation de réunion n'a pas à peupler
   *  le plan d'action du projet pour tous ses lecteurs. */
  is_confidential: true;
  project_ids: number[];
  committee_ids: number[];
  structure_ids: number[];
  /** Guide d'origine, pour marquer « action créée » après enregistrement. */
  meeting_guide: { meeting_id: number; guide_id: number };
  /** Note système posée par le serveur à la création (auteur « — »). */
  traceability_note: string;
}

/** True si ce guide propose une action à créer. */
export function hasStandardAction(guide: GuideItem): boolean {
  return guide.action_label.trim().length > 0;
}

/**
 * Date d'échéance : date de réunion décalée de l'écart du guide.
 *
 * Calcul en UTC (`Date.UTC`) plutôt qu'en heure locale : `new Date('2026-08-20')`
 * est déjà interprété en UTC, et repasser par le fuseau local ferait basculer
 * d'un jour à l'ouest de Greenwich.
 */
export function actionDeadline(meetingDate: string, delayDays: number): string | null {
  const [y, m, d] = meetingDate.split('-').map(Number);
  if (!y || !m || !d) return null;
  const base = new Date(Date.UTC(y, m - 1, d));
  base.setUTCDate(base.getUTCDate() + delayDays);
  return base.toISOString().slice(0, 10);
}

/**
 * Réalisateurs (R) de l'action : union des rôles cochés sur le guide.
 *
 * Union et non concaténation — un animateur est souvent aussi participant, et
 * un identifiant en double fausserait la RACI.
 */
export function actionPerformers(guide: GuideItem, ctx: MeetingContext): number[] {
  const ids = new Set<number>();
  if (guide.action_for_animators) ctx.animatorIds.forEach((id) => ids.add(id));
  if (guide.action_for_participants) ctx.participantIds.forEach((id) => ids.add(id));
  return [...ids];
}

/** Date ISO en JJ/MM/AAAA, comme partout ailleurs dans l'app. */
function frDate(iso: string): string {
  const [y, m, d] = iso.split('-');
  return y && m && d ? `${d}/${m}/${y}` : iso;
}

/**
 * Libellé de l'action, éventuellement préfixé par la réunion d'origine.
 *
 * « [COPIL — Refonte réseau - 20/08/2026] Valider le CR ». Sortie de la fiche
 * réunion, une action de préparation n'a plus rien qui dise d'où elle vient :
 * le préfixe le porte, dans le seul champ qui voyage partout.
 *
 */
export function actionText(guide: GuideItem, ctx: MeetingContext): string {
  const label = guide.action_label.trim();
  if (!guide.action_quote_meeting) return label;
  return `[${ctx.meetingLabel} - ${frDate(ctx.meetingDate)}] ${label}`;
}

/** Pré-remplissage complet de l'action proposée par ce guide. */
export function buildActionPrefill(
  guide: GuideItem,
  ctx: MeetingContext,
  meetingId: number,
  todoStatusId: number | null,
): ActionPrefill {
  return {
    text: actionText(guide, ctx),
    deadline: actionDeadline(ctx.meetingDate, guide.action_delay_days),
    status_id: todoStatusId,
    priority: ACTION_PRIORITY_NORMAL,
    approver_id: ctx.currentUserId,
    performers: actionPerformers(guide, ctx),
    consulted: [],
    informed: [],
    is_confidential: true,
    // Même périmètre que la réunion. Le programme n'a pas d'équivalent sur une
    // action : il se déduit du projet, et se perd si la réunion n'en a pas.
    project_ids: ctx.projectId ? [ctx.projectId] : [],
    committee_ids: ctx.committeeId ? [ctx.committeeId] : [],
    structure_ids: ctx.structureId ? [ctx.structureId] : [],
    meeting_guide: { meeting_id: meetingId, guide_id: guide.id },
    traceability_note: meetingOriginNote(ctx),
  };
}

/**
 * Contexte de création d'action à partir de l'état du formulaire de réunion.
 *
 * `null` quand l'utilisateur ne peut pas créer d'action — le CTA est alors
 * masqué plutôt que de mener à un 403 à l'enregistrement.
 */
export function meetingContextFrom(
  state: MeetingFormState,
  currentUserId: number | null,
  allowed: boolean,
): MeetingContext | null {
  if (!allowed || !currentUserId) return null;
  return {
    meetingLabel: state.label,
    meetingDate: state.meetingDate,
    animatorIds: state.animatorIds,
    participantIds: state.participantIds,
    // 0 = « aucun » dans le formulaire (les selects portent une option vide).
    projectId: state.projectId || null,
    committeeId: state.committeeId || null,
    structureId: state.structureId || null,
    currentUserId,
  };
}
